import React from 'react';
import { equals, clone } from 'ramda';
import {
  IAnalys,
  IColumn,
  ISumData,
} from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import {
  ILocalAnalys,
  IFilledLocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  IUIUrval,
  IBooleanConditionObject,
} from '../../../../../../../Types/UrvalTypes';
import { AvenyCollectionMap } from '../../../../../../../Model/ModelContext/ModelContext';
import { ISelection } from '../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import {
  urvalFromAttributeNames,
  urvalFromKodplan,
} from '../../../../../../../Components/Urval/Utils/urvalUtils';
import { ISelectedAnalys } from '../../EkonomiskAnalysPage';
import { urvalUnchanged } from '../SideMenu/Components/UrvalOchSummering/Components/Urval/Utils/UrvalUtils';
import { kodplaner_ekkodplaner } from '../../../../../../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import { unfilledEkonomiskDateToFilled } from '../../Utils/convertUtils';
import { StandardColumnType } from '../SideMenu/Components/PeriodOchKolumner/EditColumn/Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import {
  getDateInputType,
  hasAnyInterval,
  getDateInputTypeError,
  analysEquals,
} from '../../Utils/analysUtils';
import {
  getUserString,
  datesWithUpdatedUserStrings,
  updateDatesByDateInputType,
  restrictDateString,
  datePartsToCompletedDateString,
  dateInputTypeWasRaised,
  getDefaultDate,
  DateInputType,
} from '../../../../../../../Utils/dateUtils';
import EkonomiskAnalysDateErrorHandler, {
  DateElementType,
} from '../EkonomiskAnalysDateErrorHandler/EkonomiskAnalysDateErrorHandler';
import { convertTEDUrvalToUIUrval } from '../../../../../../../Components/Urval/Utils/convertTEDUrvalToUIUrval';
import { EK_KONTO_URVAL_COLLECTION_NAME } from '../../../../../../../Components/Urval/Consts/UrvalConsts';
import EnhancedBokforingsarActions, {
  RefetchPeriodsFnType,
  IsPeriodLockedFnType,
} from '../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import { NEW_ANALYS_RAPPORT_ID } from '../SideMenu/Components/CreateNewAnalys/CreateNewAnalysUtils';
import shortid from 'shortid';
import {
  BudgetSummationModeType,
  SummationModeType,
  defaultBudgetSummationMode,
} from '../../Utils/SummationModeUtils';
import {
  addCalculatedColumnDependencies,
  createColumnsForFullPrevYear,
  createColumnsForPartYear,
  getUtgaendePeriodColumn,
  shouldCreateColumnsForUtgaendePrevPeriod,
} from './Utils/AnalysSettingsHandlerUtils';
import {
  ANALYSKATEGORI_ID_VISA_SAMMANFATTNING,
  AnalysLevelEnum,
  AnalysTypeEnum,
  SpecifikationTypeEnum,
} from '../../Const/EkonomiskAnalysConsts';
import type { Location } from 'history';
import { useLocation } from 'react-router';
import { IUrvalUsedForDrilling } from '../../Utils/detaljanalysUtils';

interface IProps {
  analysType: AnalysTypeEnum;
  analysLevel: AnalysLevelEnum;
  analysSettings: IAnalys | IFilledLocalAnalys;
  drilledFromDetaljanalys: boolean;
  selectedAnalys?: ISelectedAnalys | null;
  unsavedAnalys: boolean;
  availableReports: ISelection[];
  collectionMap: AvenyCollectionMap;
  loading?: boolean;
  children: (result: IAnalysSettingsContext) => React.ReactNode;
  startDates: IEkonomiskAnalysDates;
  kodplan: kodplaner_ekkodplaner;
  kodbehorighetsUrval?: IUIUrval;
  grundurvalUrval?: IUIUrval;
  sumsFromUrl: string[];
  standardColumns: StandardColumnType[] | undefined;
  setImpersonatedCodePrivilegeChanged: (value: boolean) => void;
  originalStartDates: IEkonomiskAnalysDates;
  urvalConditionsIUrvalUsedForDrilling: IUrvalUsedForDrilling[];
}

interface IInnerProps extends IProps {
  location: Location<unknown>;
}

interface IExtendedProps extends IInnerProps {
  getDateError: (
    date: IDate,
    element: DateElementType,
    intervalTyp?: DateInputType,
    required?: boolean
  ) => IDateError | undefined;
  getDateCombinationError: (
    date: IDate,
    dateError: IDateError | undefined,
    element: DateElementType,
    dates: IEkonomiskAnalysDates,
    isInterval: boolean,
    intervalTyp?: DateInputType
  ) => IDateError | undefined;
  getOtherDateElement: (element: DateElementType) => DateElementType;
  refetchPeriods: RefetchPeriodsFnType;
  isPeriodLocked: IsPeriodLockedFnType | undefined;
}

enum SettingsTypeEnum {
  UPDATED,
  ORIGINAL,
  CURRENT,
}

interface IState {
  originalSettings: IFilledLocalAnalys;
  updatedSettings: ILocalAnalys;
  currentSettings: IFilledLocalAnalys;
}

export type OnDatesChangeType = (
  dates: IEkonomiskAnalysDates,
  validateYear: (year: number, commitCallback: () => void) => void,
  shouldUpdateDirect: boolean,
  cancelCallback?: () => void
) => void;

export interface IAnalysSettingsContext {
  settingsForCurrentlyShown: IFilledLocalAnalys;
  updatedSettings: ILocalAnalys;
  originalSettings: IFilledLocalAnalys;
  kodbehorighetsUrval?: IUIUrval;
  grundurvalUrval?: IUIUrval;
  changedSections: ChangeTrackedSection[];
  changedFromOriginal: boolean;
  availableReports: ISelection[];
  onSumsChanged: (sums: ISumData[], urval: IUIUrval) => void;
  onColumnsChanged: (columns: IColumn[]) => void;
  onUrvalChanged: (urval: IUIUrval) => void;
  reset: () => void;
  update: (updateOrig?: boolean) => void;
  clearUrval: () => void;
  onFromDateChange: DateChangeFunction;
  onToDateChange: DateChangeFunction;
  onDenominationChanged: (denomination: number) => void;
  onDecimalsChanged: (decimals: number) => void;
  onExkluderaPersonalkostnaderChanged: (exkludera: boolean) => void;
  toggleShowComparison: () => void;
  toggleEndastBokfört: () => void;
  createNewAnalys: (analys: ILocalAnalys) => void;
  onBudgetSummationModeChanged: (
    summationMode: BudgetSummationModeType
  ) => void;
  onSummationModeChanged: (summationMode: SummationModeType) => void;
  setRefetchAnalys: (refetchAnalys: () => void) => void;
  onVisaSummeringarChanged: (show: boolean) => void;
  onExpandedSumIndexChange: (expandedLevel: number) => void;
}

export type ChangeTrackedSection =
  | 'summering'
  | 'urval'
  | 'other'
  | 'periodKolumner';

export interface IEkonomiskAnalysFilledDates {
  from: IFilledDate;
  to: IFilledDate;
}

export interface IEkonomiskAnalysDates {
  from: IDate;
  to: IDate;
}

export interface IDate {
  year?: number;
  month?: number;
  day?: number;
  userString: string;
  error?: IDateError;
}

export interface IFilledDate {
  year: number;
  month: number;
  day?: number;
}

interface IDateError {
  text: string;
  type: DateInputType;
}

export type DateChangeFunction = (
  date: IDate,
  validateYear: (
    year: number,
    commitCallback: () => void,
    cancelCallback?: () => void
  ) => void,
  changeBoth?: boolean
) => void;

type ChangeableMiscSettings = 'denomination' | 'decimals' | 'showComparison';

export const fromFilledToUnfilledLocalAnalys = (
  filled: IFilledLocalAnalys,
  getDateError?: (
    date: IDate,
    element: DateElementType,
    intervalTyp?: DateInputType,
    required?: boolean
  ) => IDateError | undefined
): ILocalAnalys => {
  const newFilled = clone(filled);
  const { from, to } = newFilled.dates;
  let dateError: IDateError | undefined;
  const userString = getUserString(
    {
      year: from.year,
      month: from.month,
      day: from.day,
      userString: `ghgfhg`,
    },
    filled.dateInputType,
    true
  );
  if (getDateError) {
    dateError = getDateError(
      { ...from, userString: userString },
      'from',
      newFilled.dateInputType,
      true
    );
  }
  return {
    ...filled,
    dates: {
      from: {
        ...newFilled.dates.from,
        userString: userString,
        error: dateError,
      },
      to: {
        ...newFilled.dates.to,
        userString: getUserString(
          { year: to.year, month: to.month, day: to.day, userString: '' },
          filled.dateInputType
        ),
      },
    },
  };
};

class AnalysSettingsHandler extends React.PureComponent<
  IExtendedProps,
  IState
> {
  refetchAnalys?: () => void;

  constructor(props: IExtendedProps) {
    super(props);

    if (
      //FIXME, kan  lösas med typeguards?
      //eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore : .dates finns inte på IAnalys, därför vi kollar typed med den
      this.props.analysSettings.dates &&
      this.props.selectedAnalys?.analys.id !== NEW_ANALYS_RAPPORT_ID
    ) {
      (this.props.analysSettings as ILocalAnalys).urval = this.urvalFromSums(
        this.props.analysSettings
      );
    }

    //FIXME, kan  lösas med typeguards?
    //eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore : .dates finns inte på IAnalys, därför vi kollar typed med den
    if (this.props.analysSettings.dates) {
      const filledOriginalSettings = this.props
        .analysSettings as IFilledLocalAnalys;

      this.state = {
        originalSettings: filledOriginalSettings,
        updatedSettings: fromFilledToUnfilledLocalAnalys(
          filledOriginalSettings,
          props.getDateError
        ),
        currentSettings: clone(filledOriginalSettings),
      };
    } else {
      const defaultSumsForDetaljAnalys =
        this.props.analysSettings.categoryID ===
          ANALYSKATEGORI_ID_VISA_SAMMANFATTNING &&
        this.props.analysLevel === AnalysLevelEnum.UNSET
          ? ['0']
          : this.props.sumsFromUrl;

      const urval =
        this.props.analysSettings.type === 'RAPPORTDIMENSIONLOPANDE'
          ? this.props.analysSettings.sparatAnvandarUrval
            ? this.urvalFromSavedUrval(
                this.props.analysSettings.sparatAnvandarUrval
              )
            : this.urvalFromSums(
                this.props.analysSettings,
                this.props.analysLevel,
                this.props.urvalConditionsIUrvalUsedForDrilling
              )
          : urvalFromKodplan(this.props.kodplan, this.props.collectionMap);

      const updatedSettings = this.fromAnalysToAnalysSettings(
        props.analysSettings,
        urval,
        SettingsTypeEnum.UPDATED,
        undefined,
        undefined,
        undefined,
        defaultSumsForDetaljAnalys,
        this.props.analysLevel
      );

      const originalSettings =
        this.fromUnfilledToFilledLocalAnalys(updatedSettings);

      if (originalSettings) {
        this.state = {
          originalSettings,
          updatedSettings,
          currentSettings: clone(originalSettings),
        };
      }
    }
  }

  onExpandedSumIndexChange = (expandedLevel: number) => {
    this.setState(prevState => {
      // Användaren kan klicka bort lägsta nivån
      const expandedSumLevel =
        expandedLevel === prevState.updatedSettings.other.expandedSumIndex
          ? Math.max(0, expandedLevel - 1)
          : expandedLevel;

      return {
        updatedSettings: {
          ...prevState.updatedSettings,
          other: {
            ...prevState.updatedSettings.other,
            expandedSumIndex: expandedSumLevel,
          },
        },
      };
    });
  };

  componentDidUpdate(prevProps: IExtendedProps, prevState: IState) {
    if (prevProps.startDates !== this.props.startDates) {
      this.setState(prevState => ({
        updatedSettings: {
          ...prevState.updatedSettings,
          dates: {
            from: {
              ...this.props.startDates.from,
              userString: restrictDateString(
                this.props.startDates.from.userString,
                prevState.updatedSettings.dateInputType
              ),
            },
            to: {
              ...this.props.startDates.to,
              userString: restrictDateString(
                this.props.startDates.to.userString,
                prevState.updatedSettings.dateInputType
              ),
            },
          },
        },
      }));
    }

    if (!this.props.unsavedAnalys) {
      if (
        !equals(prevProps.selectedAnalys, this.props.selectedAnalys) ||
        prevProps.analysSettings.name !== this.props.analysSettings.name ||
        // Vi kan få dålig data under loading tiden, men vill fortfarande rendera men aldrig ändra till settings som finns under loadingtiden
        (this.props.loading !== prevProps.loading && !this.props.loading)
      ) {
        //FIXME, kan  lösas med typeguards?
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore : .dates finns inte på IAnalys, därför vi kollar typed med den
        if (this.props.analysSettings.dates) {
          const originalSettings = {
            ...this.props.analysSettings,
          } as IFilledLocalAnalys;

          this.setState({
            originalSettings: originalSettings,
            updatedSettings: fromFilledToUnfilledLocalAnalys(
              originalSettings,
              this.props.getDateError
            ),
            currentSettings: clone(originalSettings),
          });
        } else {
          const defaultSumsForDetaljAnalys =
            this.props.analysSettings.categoryID ===
              ANALYSKATEGORI_ID_VISA_SAMMANFATTNING &&
            this.props.analysLevel === AnalysLevelEnum.UNSET
              ? ['0']
              : this.props.sumsFromUrl;

          const urval =
            this.props.analysSettings.type === 'RAPPORTDIMENSIONLOPANDE'
              ? this.props.analysSettings.sparatAnvandarUrval
                ? this.urvalFromSavedUrval(
                    this.props.analysSettings.sparatAnvandarUrval
                  )
                : this.urvalFromSums(
                    this.props.analysSettings,
                    this.props.analysLevel,
                    this.props.urvalConditionsIUrvalUsedForDrilling
                  )
              : urvalFromKodplan(this.props.kodplan, this.props.collectionMap);

          const inputTypeWasRaised = dateInputTypeWasRaised(
            prevState.updatedSettings.dateInputType,
            this.props.analysSettings.dateInputType
          );

          const updatedSettings = this.fromAnalysToAnalysSettings(
            this.props.analysSettings,
            urval,
            SettingsTypeEnum.UPDATED,
            !(
              inputTypeWasRaised &&
              prevState.updatedSettings.dateInputType === DateInputType.year
            ),
            true,
            this.props.analysSettings.dateInputType !== undefined
              ? this.props.analysSettings.dateInputType
              : prevState.updatedSettings.dateInputType,
            defaultSumsForDetaljAnalys,
            this.props.analysLevel
          );

          const currentSettings = this.fromUnfilledToFilledLocalAnalys(
            this.fromAnalysToAnalysSettings(
              this.props.analysSettings,
              urval,
              SettingsTypeEnum.CURRENT,
              !inputTypeWasRaised &&
                (!this.props.startDates.from.error ||
                  (this.props.analysSettings.isInterval &&
                    !this.props.startDates.to.error)),
              undefined,
              this.props.analysSettings.dateInputType !== undefined
                ? this.props.analysSettings.dateInputType
                : prevState.updatedSettings.dateInputType
            )
          );

          const originalSettings = this.fromUnfilledToFilledLocalAnalys(
            this.fromAnalysToAnalysSettings(
              this.props.analysSettings,
              urval,
              SettingsTypeEnum.ORIGINAL
            )
          );

          if (originalSettings && currentSettings) {
            this.setState({
              originalSettings,
              updatedSettings,
              currentSettings,
            });
          }

          //Uppdatera automatiskt när vi öppnar EkA i en ny tabb genom detaljanalys
          if (
            this.props.analysLevel !== AnalysLevelEnum.UNSET ||
            this.props.drilledFromDetaljanalys
          ) {
            this.updateReport();
          }
        }
      }
    }
  }

  /** Handle differently depending on intervalTyp? */
  fromUnfilledToFilledLocalAnalys = (
    unfilled: ILocalAnalys
  ): IFilledLocalAnalys | null => {
    const { from, to } = unfilled.dates;
    if (from.year && from.month && to.year && to.month) {
      return {
        ...unfilled,
        dates: {
          from: {
            year: from.year,
            month: from.month,
            day: from.day,
          },
          to: {
            year: to.year,
            month: to.month,
            day: to.day,
          },
        },
      };
    } else {
      return null;
    }
  };

  getDefaultDates = (
    isInterval: boolean,
    useStartDates?: boolean,
    intervalTyp?: DateInputType,
    saveUserInput?: boolean
  ): IEkonomiskAnalysDates => {
    const query = new URLSearchParams(this.props.location.search);
    const queryFromYear = query.get('fromYear');
    const queryFromMonth = query.get('fromMonth');
    const queryFromDay = query.get('fromDay');
    const queryToYear = query.get('toYear');
    const queryToMonth = query.get('toMonth');
    const queryToDay = query.get('toDay');
    const { from, to } = this.props.startDates;
    const { from: defaultFrom, to: defaultTo } = this.props.originalStartDates;

    const analysLevelParam = query.get('analysLevel');
    const derivesFromDetaljanalys = analysLevelParam != undefined;

    const fromYear = queryFromYear
      ? parseInt(queryFromYear, 10)
      : useStartDates
      ? from.year
        ? from.year
        : defaultFrom.year
      : defaultFrom.year;
    const toYear = queryToYear
      ? parseInt(queryToYear, 10)
      : useStartDates
      ? to.year
        ? to.year
        : defaultTo.year
      : defaultTo.year;
    const fromMonth = queryFromMonth
      ? parseInt(queryFromMonth, 10)
      : useStartDates
      ? from.month
        ? from.month
        : defaultFrom.month
      : defaultFrom.month;
    const toMonth = queryToMonth
      ? parseInt(queryToMonth, 10)
      : useStartDates
      ? to.month
        ? to.month
        : defaultTo.month
      : defaultTo.month;
    /*Days should also come from query? */
    const fromDay = queryFromDay
      ? parseInt(queryFromDay, 10)
      : useStartDates
      ? from.day
        ? from.day
        : defaultFrom.day
      : defaultFrom.day;
    const toDay = queryToDay
      ? parseInt(queryToDay, 10)
      : useStartDates
      ? to.day
        ? to.day
        : defaultFrom.day
      : defaultFrom.day;

    const dateObject: IEkonomiskAnalysDates = {
      from: {
        year: fromYear,
        month: fromMonth,
        day: fromDay,
        userString:
          saveUserInput && !derivesFromDetaljanalys
            ? getUserString(
                useStartDates
                  ? this.props.startDates.from
                  : this.props.originalStartDates.from,
                intervalTyp
              )
            : getUserString(
                {
                  year: fromYear,
                  month: fromMonth,
                  day: fromDay,
                  userString: '',
                },
                intervalTyp,
                true
              ),
        error: saveUserInput ? from.error : undefined,
      },
      to: {
        year: toYear,
        month: toMonth,
        day: toDay,
        userString: !isInterval
          ? ''
          : saveUserInput
          ? getUserString(this.props.startDates.to, intervalTyp)
          : getUserString(
              { year: toYear, month: toMonth, day: toDay, userString: '' },
              intervalTyp
            ),
        error: !isInterval ? undefined : saveUserInput ? to.error : undefined,
      },
    };

    if (!isInterval) {
      return {
        ...dateObject,
        to: {
          year: fromYear,
          month: fromMonth,
          day: fromDay,
          userString: '',
          error: undefined,
        },
      };
    }

    return dateObject;
  };

  urvalFromSavedUrval = (urval: string): IUIUrval => {
    return convertTEDUrvalToUIUrval(
      EK_KONTO_URVAL_COLLECTION_NAME,
      JSON.parse(urval),
      this.props.collectionMap
    ).unsafelyUnwrap();
  };

  urvalFromSums = (
    analys: IAnalys | ILocalAnalys,
    analysLevel?: AnalysLevelEnum,
    urvalConditionsIUrvalUsedForDrilling?: IUrvalUsedForDrilling[]
  ): IUIUrval => {
    let filteredSums: ISumData[] = analys.sums;
    if (analys.categoryID === ANALYSKATEGORI_ID_VISA_SAMMANFATTNING) {
      filteredSums = analys.sums.filter(sum => {
        return urvalConditionsIUrvalUsedForDrilling?.some(
          condition => condition.attributeDisplayName === sum.Namn
        );
      });
    }

    const attributes = filteredSums
      .map(sum => sum.attributename)
      .filter(name => name !== null) as string[];

    const urval = urvalFromAttributeNames(
      this.props.collectionMap,
      attributes,
      analysLevel,
      urvalConditionsIUrvalUsedForDrilling
    );

    return urval;
  };

  fromAnalysToAnalysSettings = (
    analys: IAnalys,
    urval: IUIUrval,
    settingsType: SettingsTypeEnum,
    useStartDates?: boolean,
    saveUserInput?: boolean,
    dateInputType?: DateInputType,
    sumsFromUrl?: string[],
    analysLevel?: AnalysLevelEnum
  ): ILocalAnalys => {
    const dates = updateDatesByDateInputType(
      this.getDefaultDates(
        analys.isInterval,
        useStartDates,
        analys.dateInputType,
        saveUserInput
      ),
      this.props.originalStartDates,
      analys.dateInputType
    );
    // Ta fram rätt summeringsnivåner utifrån listan som skickas med löpnr i url
    let sums = analys.sums;
    //Sätt upp rätt expandendSumIndex
    let expandToIndex = analys.expandToIndex ?? 0;
    if (
      settingsType === SettingsTypeEnum.UPDATED &&
      sumsFromUrl &&
      sumsFromUrl.length > 0
    ) {
      sums = sums.filter(sumData =>
        sumsFromUrl.includes(sumData.LOPNR?.toString() ?? '')
      );
      if (analysLevel && analysLevel !== AnalysLevelEnum.UNSET) {
        expandToIndex = parseInt(sumsFromUrl[sumsFromUrl.length - 1]);
      }
    }

    return {
      ...analys,
      sums,
      dateInputType:
        dateInputType !== undefined ? dateInputType : analys.dateInputType,
      urval,
      dates,
      other: {
        budgetSummationMode: defaultBudgetSummationMode,
        decimals: analys.defaultSettings.decimals,
        denomination: analys.defaultSettings.denomination,
        includeEjBokfordaJournaler:
          analys.defaultSettings.includeEjBokfordaJournaler,
        showComparison: analys.defaultSettings.showComparison,
        summationMode: analys.defaultSettings.summationMode,
        exkluderaPersonalKostnader:
          analys.defaultSettings.exkluderaPersonalKostnader,
        expandedSumIndex: expandToIndex,
      },
    };
  };

  onBudgetSummationModeChanged = (
    budgetSummationMode: BudgetSummationModeType
  ) => {
    this.setState(prevState => ({
      updatedSettings: {
        ...prevState.updatedSettings,
        other: {
          ...prevState.updatedSettings.other,
          budgetSummationMode: budgetSummationMode,
        },
      },
    }));
  };

  onSummationModeChanged = (summationMode: SummationModeType) => {
    this.setState(prevState => ({
      updatedSettings: {
        ...prevState.updatedSettings,
        other: { ...prevState.updatedSettings.other, summationMode },
      },
    }));
  };

  onUrvalChanged = (urval: IUIUrval) => {
    this.setState(prevState => ({
      updatedSettings: { ...prevState.updatedSettings, urval },
    }));
  };

  clearUrval = () => {
    this.setState(prevState => ({
      updatedSettings: { ...prevState.updatedSettings, urval: null },
    }));
  };

  clearDisplayLabel = (columns: IColumn[]) => {
    return columns.map(col => ({ ...col, displayLabel: undefined }));
  };

  // Updates state if updateNow is true
  updateReportAfterStateChange = (updateNow: boolean) => {
    if (updateNow) {
      this.updateReport();
    }
  };

  onDateChange =
    (element: 'from' | 'to'): DateChangeFunction =>
    (
      date: IDate,
      validateYear: (
        year: number,
        commitCallback: () => void,
        cancelCallback?: () => void
      ) => void,
      changeBoth: boolean = false
    ): void => {
      const updatedSettings = clone(this.state.updatedSettings);
      const completedDateString = restrictDateString(
        datePartsToCompletedDateString(date),
        updatedSettings.dateInputType
      );
      if (completedDateString !== '') {
        date.userString = completedDateString;
      }
      const dateError = this.props.getDateError(
        date,
        element,
        this.state.updatedSettings.dateInputType,
        element === 'from'
      );

      let error = dateError;

      const updateBoth =
        element === 'from' && updatedSettings.dates.to.userString === '';

      if (!date.year) {
        if (date.userString === '' && element === 'to') {
          updatedSettings.dates[element] = {
            ...this.state.updatedSettings.dates.from,
            userString: date.userString,
            error,
          };
        }
        if (element === 'to' && !error) {
          updatedSettings.dates[element] = {
            ...updatedSettings.dates[this.props.getOtherDateElement(element)],
            userString: '',
            error: undefined,
          };
        } else {
          updatedSettings.dates[element] = {
            userString: date.userString,
            error,
          };
        }

        // changeBoth borde kunna tas bort
        if (updateBoth || changeBoth) {
          updatedSettings.dates[this.props.getOtherDateElement(element)] = {
            userString: '',
          };
        }

        if (
          this.props.isPeriodLocked &&
          this.props.isPeriodLocked(
            updatedSettings.dates[element].year,
            updatedSettings.dates.from.month,
            updatedSettings.dates.to.month
          )
        ) {
          updatedSettings.other.includeEjBokfordaJournaler = false;
        }

        this.setState({
          updatedSettings: {
            ...updatedSettings,
            columns: this.clearDisplayLabel(updatedSettings.columns),
          },
        });
      } else {
        const updateYearCallback = (yearValid: boolean = true) => {
          const month: number = date.month !== undefined ? date.month : 1;
          const day: number = date.day !== undefined ? date.day : 1;

          if (!yearValid) {
            error = { text: 'Ogiltig period', type: DateInputType.year };
          }

          if (updateBoth || changeBoth) {
            updatedSettings.dates[element] = {
              ...date,
              month,
              day,
              error,
            };

            updatedSettings.dates[this.props.getOtherDateElement(element)] = {
              ...date,
              month,
              day,
              error: undefined,
              userString: '',
            };
          } else {
            const combinationError = this.props.getDateCombinationError(
              date,
              dateError,
              element,
              updatedSettings.dates,
              updatedSettings.isInterval,
              this.state.updatedSettings.dateInputType
            );

            updatedSettings.dates[element] = {
              ...date,
              month,
              day,
              error: error ? error : combinationError,
            };
          }
          updatedSettings.columns = this.clearDisplayLabel(
            updatedSettings.columns
          );

          if (
            this.props.isPeriodLocked &&
            this.props.isPeriodLocked(
              updatedSettings.dates[element].year,
              updatedSettings.dates.from.month,
              updatedSettings.dates.to.month
            )
          ) {
            updatedSettings.other.includeEjBokfordaJournaler = false;
          }

          this.setState({
            updatedSettings,
          });
        };

        validateYear(date.year, updateYearCallback, () => {
          updateYearCallback(false);
        });
      }
    };

  // Tycker att det här är field members, inte metoder så behöver vara överst i filen...
  onFromDateChange = this.onDateChange('from');
  onToDateChange = this.onDateChange('to');

  getChangedSections = (
    sumsFromUrl: string[],
    ignoreChanges: boolean
  ): ChangeTrackedSection[] => {
    const changedSections: ChangeTrackedSection[] = [];
    const original = this.state.originalSettings;
    const updated = this.state.updatedSettings;

    if (this.props.unsavedAnalys || ignoreChanges) {
      return [];
    }

    // summering
    if (
      (!equals(original.sums, updated.sums) ||
        original.visaSummeringar !== updated.visaSummeringar ||
        original.other.expandedSumIndex !== updated.other.expandedSumIndex) &&
      sumsFromUrl.length === 0
    ) {
      changedSections.push('summering');
    }

    // urval
    if (!urvalUnchanged(updated.urval, original.urval, true)) {
      changedSections.push('urval');
    }

    // Period och kolumner
    // Ignorera displayLabel pga parsningen av rubriker
    if (
      !equals(
        original.columns.map(col => ({ ...col, displayLabel: undefined })),
        updated.columns.map(col => ({ ...col, displayLabel: undefined }))
      )
      /* ||
      !equals(original.dates, updated.dates)*/
    ) {
      changedSections.push('periodKolumner');
    }

    // other
    if (
      !equals(
        { ...original.other, expandedSumIndex: 0 },
        { ...updated.other, expandedSumIndex: 0 }
      )
    ) {
      changedSections.push('other');
    }

    return changedSections;
  };

  onColumnsChanged = (columns: IColumn[]) => {
    let newAnalysSettings = clone(this.state.updatedSettings);
    const newDateInputType = getDateInputType(
      columns,
      this.props.standardColumns
    );
    const newIsInterval = hasAnyInterval(columns, this.props.standardColumns);
    const changedFromInterval = !newIsInterval && newAnalysSettings.isInterval;
    const changedToInterval = newIsInterval && !newAnalysSettings.isInterval;
    const inputTypeWasRaised = dateInputTypeWasRaised(
      newAnalysSettings.dateInputType,
      newDateInputType
    );

    const newColumns = changedFromInterval
      ? this.clearDisplayLabel(columns)
      : columns;

    newAnalysSettings.dates = updateDatesByDateInputType(
      newAnalysSettings.dates,
      this.props.originalStartDates,
      newDateInputType
    );

    newAnalysSettings.isInterval = newIsInterval;

    const newFromDateUserString = getUserString(
      newAnalysSettings.dates.from,
      newDateInputType,
      true
    );
    const newToDateUserString = changedToInterval
      ? ''
      : getUserString(newAnalysSettings.dates.to, newDateInputType);

    const defaultFromDate = {
      ...datesWithUpdatedUserStrings(
        getDefaultDate(newAnalysSettings.dates, newDateInputType),
        newDateInputType
      ).from,
    };

    const fromDate = {
      ...newAnalysSettings.dates.from,
      userString: newFromDateUserString,
      error: getDateInputTypeError(
        newAnalysSettings.dates.from.error,
        newDateInputType
      ),
    };

    const defaultToDate = {
      ...datesWithUpdatedUserStrings(
        getDefaultDate(newAnalysSettings.dates, newDateInputType),
        newDateInputType
      ).to,
    };
    if (
      inputTypeWasRaised &&
      newAnalysSettings.dateInputType === DateInputType.year
    ) {
      defaultToDate.userString = '';
    }
    const toDate = {
      ...newAnalysSettings.dates.to,
      userString: newToDateUserString,
      error: changedToInterval
        ? undefined
        : getDateInputTypeError(
            newAnalysSettings.dates.to.error,
            newDateInputType
          ),
    };

    this.setState({
      updatedSettings: {
        ...newAnalysSettings,
        columns: newColumns,
        dateInputType: newDateInputType,
        dates: {
          from: inputTypeWasRaised ? defaultFromDate : fromDate,
          to: inputTypeWasRaised
            ? defaultToDate
            : changedFromInterval
            ? fromDate
            : toDate,
        },
      },
    });
  };

  onSumsChanged = (sums: ISumData[], urval: IUIUrval) => {
    this.setState(prevState => ({
      updatedSettings: {
        ...prevState.updatedSettings,
        sums,
        urval,
      },
    }));
  };

  onExkluderaPersonalkostnaderChanged = (exkludera: boolean) => {
    // Lägg till PersonalkostnaderExkluderade på alla sidor i användarurvalet
    const urval = clone(this.state.updatedSettings.urval);
    const attribute = 'PersonalkostnaderExkluderade';
    const attr = this.props.collectionMap.get('ekkonto')?.Attributes[attribute];
    if (attr) {
      if (exkludera) {
        urval?.pages.forEach(page => {
          page.conditionObjects.unshift({
            id: shortid.generate(),
            type: 'Boolean',
            attribute: attr,
            condition: { yes: true },
            hideInSimpleUrval: true,
            disableInUrval: true,
          } as IBooleanConditionObject);
        });
      } else {
        // Ta bort PersonalkostnaderExkluderade på alla sidor i användarurvalet
        if (urval) {
          urval.pages = urval.pages.map(page => {
            page.conditionObjects = page.conditionObjects.filter(obj => {
              return !(
                obj.type === 'Boolean' &&
                obj.attribute.AttributeName === attr.AttributeName
              );
            });
            return page;
          });
        }
      }
    }

    this.setState(prevState => ({
      updatedSettings: {
        ...prevState.updatedSettings,
        urval,
        other: {
          ...prevState.updatedSettings.other,
          exkluderaPersonalKostnader: exkludera,
        },
      },
    }));
  };

  onVisaSummeringarChanged = (show: boolean) => {
    this.setState(prevState => ({
      updatedSettings: {
        ...prevState.updatedSettings,
        visaSummeringar: show,
      },
    }));
  };

  onDenominationChanged = (denomination: number) => {
    this.changeableWithoutUpdate('denomination', denomination);
  };

  onDecimalsChanged = (decimals: number) => {
    this.changeableWithoutUpdate('decimals', decimals);
  };

  toggleShowComparison = () => {
    this.changeableWithoutUpdate('showComparison');
  };

  /** Change current setting directly, without the need to update the analys */
  changeableWithoutUpdate = (
    setting: ChangeableMiscSettings,
    newValue?: number | boolean
  ) => {
    const shouldToggle = newValue === undefined;
    this.setState(oldState => ({
      updatedSettings: {
        ...oldState.updatedSettings,
        other: {
          ...oldState.updatedSettings.other,
          [setting]: shouldToggle
            ? !oldState.updatedSettings.other[setting]
            : newValue,
        },
      },
      currentSettings: {
        ...oldState.currentSettings,
        other: {
          ...oldState.currentSettings.other,
          [setting]: shouldToggle
            ? !oldState.currentSettings.other[setting]
            : newValue,
        },
      },
    }));
  };

  createNewAnalys = (analys: ILocalAnalys) => {
    const newAnalys = clone(analys);
    newAnalys.dates = datesWithUpdatedUserStrings(
      newAnalys.dates,
      newAnalys.dateInputType
    );
    const originalAnalys = this.fromUnfilledToFilledLocalAnalys(newAnalys);
    if (originalAnalys) {
      this.setState({
        originalSettings: originalAnalys,
        currentSettings: clone(originalAnalys),
        updatedSettings: newAnalys,
      });
    }
  };

  toggleEndastBokfört = () => {
    this.setState(oldState => ({
      updatedSettings: {
        ...oldState.updatedSettings,
        other: {
          ...oldState.updatedSettings.other,
          includeEjBokfordaJournaler:
            !oldState.updatedSettings.other.includeEjBokfordaJournaler,
        },
      },
    }));
  };

  reset = () => {
    this.setState(prevState => ({
      updatedSettings: fromFilledToUnfilledLocalAnalys(
        prevState.originalSettings
      ),
    }));
  };

  setRefetchAnalys = (refetchAnalys: () => void) => {
    this.refetchAnalys = refetchAnalys;
  };

  updateReport = (updateOrig?: boolean) => {
    const sammanfattningsMode =
      this.props.analysSettings.categoryID ===
      ANALYSKATEGORI_ID_VISA_SAMMANFATTNING;

    // Never update original settings in detaljanalys
    if (sammanfattningsMode) {
      updateOrig = false;
    }

    this.props.refetchPeriods().then(() => {
      this.setState((prevState: IState) => {
        this.props.setImpersonatedCodePrivilegeChanged(false);

        const { dates } = prevState.updatedSettings;
        const newDates = unfilledEkonomiskDateToFilled(dates);

        if (
          shouldCreateColumnsForUtgaendePrevPeriod(
            prevState.updatedSettings.categoryID
          )
        ) {
          //Rensa bort kolumner som har force update true från tidigare anrop
          prevState.updatedSettings.columns =
            prevState.updatedSettings.columns.filter(
              col => !col.data.FORCE_CALCULATE
            );

          const prevUtgaendeColumn = getUtgaendePeriodColumn(
            prevState.updatedSettings.columns,
            0,
            null
          );

          const prevUtgaendeIntaktColumn = getUtgaendePeriodColumn(
            prevState.updatedSettings.columns,
            0,
            SpecifikationTypeEnum.INTAKT
          );

          const prevUtgaendeKostnadColumn = getUtgaendePeriodColumn(
            prevState.updatedSettings.columns,
            0,
            SpecifikationTypeEnum.KOSTNAD
          );

          const prevYearUtgaendeColumn = getUtgaendePeriodColumn(
            prevState.updatedSettings.columns,
            -1,
            null
          );

          const utgaendeVardePrevPeriods: IColumn[] = createColumnsForPartYear(
            newDates,
            prevUtgaendeColumn,
            prevUtgaendeIntaktColumn,
            prevUtgaendeKostnadColumn
          );

          const prevYearUtgaendeVardePrevPeriods: IColumn[] =
            createColumnsForFullPrevYear(newDates, prevYearUtgaendeColumn);

          prevState.updatedSettings.columns = [
            ...prevState.updatedSettings.columns,
            ...utgaendeVardePrevPeriods,
            ...prevYearUtgaendeVardePrevPeriods,
          ];

          prevState.updatedSettings.columns = addCalculatedColumnDependencies(
            prevState.updatedSettings.columns,
            this.props.standardColumns,
            prevState.updatedSettings.dates
          );
        }

        if (newDates) {
          const originalSettings = updateOrig
            ? { ...prevState.updatedSettings, dates: newDates }
            : prevState.originalSettings;

          const newCurrentSettings = clone({
            ...prevState.updatedSettings,
            dates: newDates,
          });

          // Om analysens inställningar ej är ändrade så refetcha analysen ändå
          if (analysEquals(newCurrentSettings, this.state.currentSettings)) {
            if (this.refetchAnalys) {
              this.refetchAnalys();
            }
          }
          return {
            currentSettings: newCurrentSettings,
            originalSettings,
          };
        }

        if (
          analysEquals(prevState.currentSettings, this.state.currentSettings)
        ) {
          if (this.refetchAnalys) {
            this.refetchAnalys();
          }
        }
        return {
          currentSettings: clone(prevState.currentSettings),
          originalSettings: prevState.originalSettings,
        };
      });
    });
  };

  render() {
    const ignoreChanges =
      this.props.analysSettings.categoryID ===
      ANALYSKATEGORI_ID_VISA_SAMMANFATTNING;

    const changedSections = this.getChangedSections(
      this.props.sumsFromUrl,
      ignoreChanges
    );

    return (
      <>
        {this.props.children({
          settingsForCurrentlyShown: this.state.currentSettings,
          updatedSettings: this.state.updatedSettings,
          originalSettings: this.state.originalSettings,
          kodbehorighetsUrval: this.props.kodbehorighetsUrval,
          grundurvalUrval: this.props.grundurvalUrval,
          changedSections: changedSections,
          availableReports: this.props.availableReports,
          onSumsChanged: this.onSumsChanged,
          onColumnsChanged: this.onColumnsChanged,
          onUrvalChanged: this.onUrvalChanged,
          reset: this.reset,
          update: this.updateReport,
          clearUrval: this.clearUrval,
          onFromDateChange: this.onFromDateChange,
          onToDateChange: this.onToDateChange,
          onDenominationChanged: this.onDenominationChanged,
          onDecimalsChanged: this.onDecimalsChanged,
          toggleEndastBokfört: this.toggleEndastBokfört,
          toggleShowComparison: this.toggleShowComparison,
          changedFromOriginal: changedSections.length > 0,
          createNewAnalys: this.createNewAnalys,
          onBudgetSummationModeChanged: this.onBudgetSummationModeChanged,
          onSummationModeChanged: this.onSummationModeChanged,
          setRefetchAnalys: this.setRefetchAnalys,
          onExkluderaPersonalkostnaderChanged:
            this.onExkluderaPersonalkostnaderChanged,
          onVisaSummeringarChanged: this.onVisaSummeringarChanged,
          onExpandedSumIndexChange: this.onExpandedSumIndexChange,
        })}
      </>
    );
  }
}

const AnalysSettingsHandlerExtended = (props: IProps) => {
  const location = useLocation();
  return (
    <EnhancedBokforingsarActions>
      {({ hasExtraPeriod, refetchPeriods, isPeriodLocked }) => {
        return (
          <EkonomiskAnalysDateErrorHandler hasExtraPeriod={hasExtraPeriod}>
            {({
              getDateError,
              getDateCombinationError,
              getOtherDateElement,
            }) => (
              <AnalysSettingsHandler
                {...props}
                getDateError={getDateError}
                getDateCombinationError={getDateCombinationError}
                getOtherDateElement={getOtherDateElement}
                refetchPeriods={refetchPeriods}
                location={location}
                isPeriodLocked={isPeriodLocked}
              />
            )}
          </EkonomiskAnalysDateErrorHandler>
        );
      }}
    </EnhancedBokforingsarActions>
  );
};

export default AnalysSettingsHandlerExtended;
